import type { Entity } from "../entityInfo";
import type { Settings } from "./settings";
import type { EntityData as SimulatorEntityData } from "../newsimulator/simulator";
import type { Fleet } from "./fleet";
import { AllianceClass, CharacterClass } from "./classes";
import type { Techs } from "./types";

type EntitiesData = {[key: string]: EntityData}

type EntityData = {
    amount: number | null
    weapon: number
    shield: number
    armor: number
    cargo?: number
    speed?: number
    fuel?: number
}

const drives: { [key: string] : keyof Techs } = {
    "202": "115",
    "203": "115",
    "204": "115",
    "205": "117",
    "206": "117",
    "207": "118",
    "208": "118",
    "209": "115",
    "210": "115",
    "211": "117",
    "212": "118",
    "213": "118",
    "214": "118",
    "215": "118",
    "218": "118",
    "219": "118"
}

const militaryShips = ['204', '205', '206', '207', '215', '211', '213', '218', '219']

// create entity, the result should be compatible with the EntitiesData from the simulator
function CreateEntityData(entityId: string, entity: EntityData, entityInfo: Entity, fleet: Fleet, settings: Settings): EntityData & SimulatorEntityData {
    const fuel = baseFuel(entityId, entityInfo, fleet.techs)
    let result: EntityData & SimulatorEntityData = {
        amount: entity.amount || 0,
        weapon: entityInfo.weapon,
        shield: entityInfo.shield,
        armor: entityInfo.armour,
        cargo: entityInfo.cargoCapacity,
        fuel: fuel,
        rapidFire: entityInfo.rapidfireAgainst
    }

    // apply all weapon boosts
    result.weapon += entityInfo.weapon * (fleet.techs['109'] || 0) * .1
    result.weapon += entityInfo.weapon * entity.weapon

    // applly all shield boosts
    result.shield += entityInfo.shield * (fleet.techs['110'] || 0) * .1
    result.shield += entityInfo.shield * entity.shield

    // apply all armor boosts
    result.armor += entityInfo.armour * (fleet.techs['111'] || 0) * .1
    result.armor += entityInfo.armour * entity.armor

    // add cargo
    if (result.cargo) {
        // TODO this is incorrect on the devtools uni
        result.cargo += (entityInfo.cargoCapacity || 0) * (fleet.techs['114'] || 0) * (settings.cargoHyperspaceFactor/100)
        result.cargo += (entityInfo.cargoCapacity || 0) * (entity.cargo || 0)
    }

    // add speed
    const { speed, drive } = baseSpeedAndDrive(entityId, entityInfo, fleet.techs)
    if (speed) {
        result.speed = speedForShip(speed, drive, fleet.techs)
        result.speed += (entity.speed || 0) * (entity.speed || 0)
    }

    // add fuel
    // FIXME add the global deuterium save factor
    if (result.fuel) {
        result.fuel -= (fuel || 0) * (entity.fuel || 0)
    }

    // alliance classes
    // apply warrior boosts
    if (fleet.allianceClass == AllianceClass.Warriors) {
        result.weapon += entityInfo.weapon * .1
        result.shield += entityInfo.shield * .1
        result.armor += entityInfo.armour * .1
    }

    // apply traders boost
    if (fleet.allianceClass == AllianceClass.Traders) {
        if (result.speed && speed && ['202', '203'].includes(entityId)) {
            result.speed += speed * .1
        }
    }

    // apply researchers boost, haha

    // character classes
    // collector
    if (fleet.characterClass == CharacterClass.Collector) {
        // +100% transport speed
        if (result.speed && speed && ['202', '203'].includes(entityId)) {
            result.speed += speed * 1.0
        }

        // +25% transport cargo
        if (result.cargo && ['202', '203'].includes(entityId)) {
            result.cargo += (entityInfo.cargoCapacity || 0) * .25
        }
    }

    // general
    if (fleet.characterClass == CharacterClass.General) {
        // +100% military speed
        if (result.speed && speed && militaryShips.includes(entityId)) {
            result.speed += speed * 1.0
        }

        // +100% recycler speed
        if (result.speed && speed && entityId == '209') {
            result.speed += speed * 1.0
        }

        // -25% consumption
        if (result.fuel) {
            result.fuel -= (entityInfo.fuelUsage || 0) * .25
        }

        // +20% cargo recs and pathfinders
        if (result.cargo && ['209', '219'].includes(entityId)) {
            result.cargo += (entityInfo.cargoCapacity || 0) * .20
        }

        // +2 combat levels
        result.weapon += entityInfo.weapon * .2
        result.shield += entityInfo.shield * .2
        result.armor += entityInfo.armour * .2
    }

    // discoverer booster, haha

    // round all the numbers
    // result.amount = Math.floor(result.amount)
    // result.weapon = Math.floor(result.weapon)
    // result.shield = Math.floor(result.shield)
    // result.armor = Math.floor(result.armor)
    // result.cargo = result.cargo ? Math.floor(result.cargo) : undefined;
    // result.speed = result.speed ? Math.floor(result.speed) : undefined;
    // result.fuel = result.fuel ? Math.floor(result.fuel) : undefined;

    return result
}

function baseFuel(entityId: string, entityInfo: Entity, techs: Techs): number | undefined {
    let fuel = entityInfo.fuelUsage

    if (fuel === undefined) {
        return fuel
    }

    // impulse small cargo
    if (entityId == "202" && (techs["117"] || 0) >= 5) {
        return fuel * getDriveFactor("117") * 10
    }

    // hyper recyclers
    if (entityId == "209" && (techs["118"] || 0) >= 15) {
        return fuel * getDriveFactor("118") * 10
    }

    // impulse recyclers
    if (entityId == "209" && (techs["117"] || 0) >= 17) {
        return fuel * getDriveFactor("117") * 10
    }

    // hyper bombers
    if (entityId == "211" && (techs["118"] || 0) >= 8) {
        return fuel * getDriveFactor("118") * 10
    }

    return fuel
}

function baseSpeedAndDrive(entityId: string, entityInfo: Entity, techs: Techs): { speed?: number, drive: keyof Techs } {
    let speed = entityInfo.speed

    if (speed === undefined) {
        return { speed: speed, drive: '115' }
    }

    let drive = drives[entityId]

    // impulse small cargo
    if (entityId == "202" && (techs["117"] || 0) >= 5) {
        speed = 10000
        drive = "117"
    }

    // impulse recyclers
    if (entityId == "209" && (techs["117"] || 0) >= 17) {
        speed = 4000
        drive = "117"
    }

    // hyper recyclers
    if (entityId == "209" && (techs["118"] || 0) >= 15) {
        speed = 6000
        drive = "118"
    }

    // hyper bombers
    if (entityId == "211" && (techs["118"] || 0) >= 8) {
        speed = 5000
        drive = "118"
    }

    return { speed: speed, drive: drive }
}

function speedForShip(speed: number, drive: keyof Techs, techs: Techs): number {
    const factor = getDriveFactor(drive)

    return speed * (1 + (techs[drive] || 0) * factor)
}

function getDriveFactor(drive: keyof Techs): number {
    if (drive == "115") {
        return 0.1
    }
    if (drive == "117") {
        return 0.2
    }
    if (drive == "118") {
        return 0.3
    }

    return 0
}

export type {
    EntitiesData,
    EntityData
}

export {
    CreateEntityData
}